import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { RouterOutlet, Router, NavigationStart, NavigationEnd } from '@angular/router';
import { OrdersService } from './modules/admin/orders/orders.service';
import { filter } from 'rxjs/operators';
import { environment } from '../environments/environment';
import { MobileInstallGateComponent } from 'app/layout/common/mobile-install-gate/mobile-install-gate.component';
import { AppUpdateService } from './core/services/app-update.service';
import {
    KIOSK_MODE_KEY,
    KIOSK_ZOOM,
    KIOSK_ZOOM_KEY,
} from './core/services/kiosk.service';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    imports: [RouterOutlet, MobileInstallGateComponent],
})
export class AppComponent implements OnInit, OnDestroy {
    private static readonly ENV_OVERLAY_STORAGE_KEY = 'env_overlay_hidden';

    readonly envName: string = (environment as any).envName ?? '';
    readonly envColor: string = (environment as any).envColor || '#f97316';
    readonly envHost: string = typeof window !== 'undefined' ? window.location.host : '';
    showEnvOverlay = false;

    constructor(
        private ordersService: OrdersService,
        private router: Router,
        private appUpdateService: AppUpdateService,
    ) {}

    ngOnInit(): void {
        this.checkTokenVersion();
        this.applyDisplayZoom();
        this.appUpdateService.init();
        // TEMP: env watermark (DEV / LOCAL) is force-hidden so the UI
        // reads cleanly during the current design review. The previous
        // localStorage-backed default-on behavior is preserved below —
        // just swap which line is commented to restore it. Ctrl+Shift+E
        // still toggles the overlay back on transiently (per-session).
        this.showEnvOverlay = false;
        // this.showEnvOverlay = localStorage.getItem(AppComponent.ENV_OVERLAY_STORAGE_KEY) !== '1';
        
        // Debug: Log all navigation events
        this.router.events
            .pipe(filter(event => event instanceof NavigationStart || event instanceof NavigationEnd))
            .subscribe(event => {
                if (event instanceof NavigationStart) {
                    console.log('🚀 NavigationStart:', event.url);
                }
                if (event instanceof NavigationEnd) {
                    console.log('✅ NavigationEnd:', event.url, '->', event.urlAfterRedirects);
                }
            });

        // ========== WebSocket: DISABLED - Uncomment to enable ==========
        /* 
        console.log('🚀 App initializing - connecting to WebSocket...');
        this.ordersService.connectWebSocket();
        
        // Load initial orders into cache
        this.ordersService.loadOrders().subscribe({
            next: () => console.log('✅ Initial orders loaded into cache'),
            error: (err) => console.error('❌ Error loading initial orders:', err)
        });
        */
    }
    
    ngOnDestroy(): void {
        // ========== WebSocket: DISABLED - Uncomment to enable ==========
        /* 
        console.log('🧹 App destroying - disconnecting WebSocket');
        this.ordersService.disconnectWebSocket();
        */
    }

    toggleEnvOverlay(): void {
        this.showEnvOverlay = !this.showEnvOverlay;
        if (this.showEnvOverlay) {
            localStorage.removeItem(AppComponent.ENV_OVERLAY_STORAGE_KEY);
        } else {
            localStorage.setItem(AppComponent.ENV_OVERLAY_STORAGE_KEY, '1');
        }
    }

    /**
     * Apply the desktop auto-zoom based on the monitor's PHYSICAL
     * resolution rather than the logical CSS width.
     *
     * Rule (per product spec): 4K-or-larger panels render at native 100%;
     * anything smaller than 4K gets a 0.9 downscale so dense desktop
     * screens (e.g. the email workspace) fit without horizontal cramping.
     *
     * Why physical and not a CSS media query: Windows display scaling
     * (e.g. 175% on a 3840×2160 panel) makes the browser report only
     * ~2194 *logical* px, so a width-based media query mistakes a real 4K
     * monitor for a small screen and zooms it out. `screen.width ×
     * devicePixelRatio` recovers the true pixel width and classifies it
     * correctly.
     *
     * Re-run on resize so dragging the window to another monitor (which
     * changes `devicePixelRatio` / `screen.width`) re-evaluates the zoom.
     */
    private applyDisplayZoom(): void {
        try {
            if (typeof window === 'undefined' || !document?.documentElement) {
                return;
            }

            const root = document.documentElement;
            const dpr = window.devicePixelRatio || 1;
            const logicalScreenWidth =
                window.screen?.width || window.innerWidth || 0;
            const physicalWidth = Math.round(logicalScreenWidth * dpr);

            // Mobile / very small viewports run their own layout — leave
            // them at native scale. 820px mirrors the prior breakpoint.
            const isDesktop = window.innerWidth >= 820;

            // 4K starts at 3840px wide. Small tolerance for odd panels /
            // rounding. 4K, 5K, 8K and ultrawides ≥ 3840 stay at 100%.
            const is4kOrLarger = physicalWidth >= 3800;

            // Kiosk (42" portrait panel) uses its own tunable zoom instead
            // of the desktop downscale. Detection is the explicit persisted
            // flag (KioskService), not orientation/device. The `kioskZoom`
            // override (set via `?kioskZoom=`) wins over the KIOSK_ZOOM
            // default so the panel can be tuned live without a rebuild.
            const isKiosk = localStorage.getItem(KIOSK_MODE_KEY) === 'true';
            const zoomOverride = parseFloat(
                localStorage.getItem(KIOSK_ZOOM_KEY) || '',
            );
            const kioskZoom =
                !isNaN(zoomOverride) && zoomOverride > 0
                    ? zoomOverride
                    : KIOSK_ZOOM;

            const zoom = isKiosk
                ? kioskZoom
                : isDesktop && !is4kOrLarger
                  ? 0.9
                  : 1;

            root.style.setProperty('--app-ui-zoom', String(zoom));
            // Inline `zoom` wins over the (now defaults-only) stylesheet.
            // Clear it at 100% so we don't leave a redundant property.
            (root.style as any).zoom = zoom === 1 ? '' : String(zoom);
        } catch {
            // Non-fatal: fall back to native 100% if anything throws.
        }
    }

    @HostListener('window:resize')
    onWindowResize(): void {
        this.applyDisplayZoom();
    }

    @HostListener('window:keydown', ['$event'])
    handleEnvOverlayShortcut(event: KeyboardEvent): void {
        if (!this.envName) {
            return;
        }
        if (event.ctrlKey && event.shiftKey && (event.key === 'E' || event.key === 'e')) {
            event.preventDefault();
            this.toggleEnvOverlay();
        }
    }

    private checkTokenVersion(): void {
        const TOKEN_VERSION = 'v2_2025_10_13';
        const currentVersion = localStorage.getItem('token_version');

        if (currentVersion !== TOKEN_VERSION) {
            const hasToken = localStorage.getItem('accessToken');

            if (hasToken) {
                console.warn('Token version mismatch detected. Clearing old authentication data.');
                localStorage.removeItem('accessToken');
                localStorage.removeItem('userData');
                localStorage.removeItem('userData_tradelist');
                localStorage.setItem('token_version', TOKEN_VERSION);
            } else {
                localStorage.setItem('token_version', TOKEN_VERSION);
            }
        }
    }
}
